import { useState, useCallback, useRef, useEffect } from 'react';
import { SmartChatState, SmartChatMessage, SmartChatConfig } from '@/types/smartChat';
import { PageContext } from '@/types/pageContext';
import { smartChatService } from '@/services/smartChatService';
import { pageAnalysisService } from '@/services/pageAnalysisService';

const defaultConfig: SmartChatConfig = {
  maxMessages: 6, // 3 pares user+assistant = 6 mensagens máximo
  debounceMs: 300,
  cacheTimeoutMs: 5 * 60 * 1000,
  apiRetries: 3
};

const STORAGE_KEY = 'smart-chat-messages';

export const useSmartChat = (config: Partial<SmartChatConfig> = {}) => {
  const finalConfig = { ...defaultConfig, ...config };
  const [state, setState] = useState<SmartChatState>({
    isOpen: false,
    messages: [],
    currentPageContext: null,
    isLoading: false,
    isTyping: false,
    error: null,
    contextualSuggestions: []
  });

  const retryCountRef = useRef(0);

  // Load messages from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const messages = JSON.parse(stored) as SmartChatMessage[];
        // Convert timestamps back to Date objects
        const hydratedMessages = messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setState(prev => ({ ...prev, messages: hydratedMessages }));
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  }, []);

  // Save messages to localStorage when messages change
  useEffect(() => {
    if (state.messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state.messages));
      } catch (error) {
        console.error('Error saving chat messages:', error);
      }
    }
  }, [state.messages]);

  const toggleChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const closeChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const updatePageContext = useCallback((pageContext: PageContext | null) => {
    setState(prev => {
      const suggestions = pageContext 
        ? pageAnalysisService.generateContextualSuggestions(pageContext)
        : [];
      
      return {
        ...prev,
        currentPageContext: pageContext,
        contextualSuggestions: suggestions
      };
    });
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    const userMessage: SmartChatMessage = {
      id: `user-${Date.now()}-${Math.random()}`,
      content: message.trim(),
      role: 'user',
      timestamp: new Date(),
      pageContext: state.currentPageContext || undefined,
      contextUsed: !!state.currentPageContext
    };

    // Add user message immediately
    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage].slice(-finalConfig.maxMessages),
      isLoading: true,
      isTyping: true,
      error: null
    }));

    try {
      const response = await smartChatService.sendContextualMessage(
        message,
        state.currentPageContext,
        state.messages
      );

      const assistantMessage: SmartChatMessage = {
        id: `assistant-${Date.now()}-${Math.random()}`,
        content: response.content,
        role: 'assistant',
        timestamp: new Date(),
        pageContext: state.currentPageContext || undefined,
        contextUsed: !!state.currentPageContext
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage].slice(-finalConfig.maxMessages),
        isLoading: false,
        isTyping: false,
        error: null
      }));

      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Retry logic
      if (retryCountRef.current < finalConfig.apiRetries) {
        retryCountRef.current++;
        setTimeout(() => sendMessage(message), 1000 * retryCountRef.current);
        return;
      }

      // Use fallback response
      const fallbackContent = smartChatService.getFallbackResponse(state.currentPageContext);
      
      const assistantMessage: SmartChatMessage = {
        id: `assistant-fallback-${Date.now()}`,
        content: fallbackContent,
        role: 'assistant',
        timestamp: new Date(),
        pageContext: state.currentPageContext || undefined,
        contextUsed: false
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage].slice(-finalConfig.maxMessages),
        isLoading: false,
        isTyping: false,
        error: 'Serviço temporariamente indisponível'
      }));
      
      retryCountRef.current = 0;
    }
  }, [state.currentPageContext, state.messages, finalConfig.maxMessages, finalConfig.apiRetries]);

  const sendSuggestion = useCallback((suggestionText: string) => {
    sendMessage(suggestionText);
  }, [sendMessage]);

  const clearMessages = useCallback(() => {
    setState(prev => ({ ...prev, messages: [] }));
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    toggleChat,
    closeChat,
    updatePageContext,
    sendMessage,
    sendSuggestion,
    clearMessages,
    clearError
  };
};